import { Container, Graphics, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useCallback, useMemo } from "react";

interface DoublingCubePixiProps {
  cubeValue: number;
  cubeOwner: string | null;
  player1Id: string; // To determine cube owner's color
  canOfferDouble: boolean;
  onOfferDouble: () => void;
  boardScale: number;
  playArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// Constants for cube dimensions
const CUBE_SIZE = 100;
const CORNER_RADIUS = 15;
const FONT_SIZE = 48;

export function DoublingCubePixi({
  cubeValue,
  cubeOwner,
  player1Id,
  canOfferDouble,
  onOfferDouble,
  boardScale,
  playArea
}: DoublingCubePixiProps) {
  // Calculate cube position (center of play area)
  const cubeX = playArea.x + playArea.width / 2;
  const cubeY = playArea.y + playArea.height / 2;

  // Cube size balanced for visibility and proportion
  const cubeSize = CUBE_SIZE * boardScale;
  const cornerRadius = CORNER_RADIUS * boardScale;

  // Determine cube colors based on ownership
  const { backgroundColor, textColor } = useMemo(() => {
    if (cubeOwner === null) {
      // Centered - red color
      return { backgroundColor: 0xff0000, textColor: 0xffffff };
    }

    if (cubeOwner === player1Id) {
      // Player 1 owns it - white
      return { backgroundColor: 0xffffff, textColor: 0x000000 };
    } else {
      // Player 2 owns it - black
      return { backgroundColor: 0x000000, textColor: 0xffffff };
    }
  }, [cubeOwner, player1Id]);

  // Border color/width for interactivity hint
  const borderWidth = canOfferDouble ? 3 * boardScale : 2 * boardScale;
  const borderColor = canOfferDouble ? 0xffd700 : backgroundColor; // Gold when clickable
  const borderAlpha = canOfferDouble ? 1.0 : 0.3;

  // Handle click
  const handlePointerDown = useCallback(() => {
    if (canOfferDouble) {
      onOfferDouble();
    }
  }, [canOfferDouble, onOfferDouble]);

  // Text style for the cube value
  const textStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: "Arial",
        fontSize: FONT_SIZE * boardScale,
        fontWeight: "bold",
        fill: textColor,
        align: "center"
      }),
    [boardScale, textColor]
  );

  return (
    <Container
      x={cubeX}
      y={cubeY}
      interactive={canOfferDouble}
      cursor={canOfferDouble ? "pointer" : "default"}
      onpointerdown={handlePointerDown}
    >
      {/* Cube background with border */}
      <Graphics
        draw={(g) => {
          g.clear();

          // Draw border
          g.lineStyle(borderWidth, borderColor, borderAlpha);

          // Draw filled rounded rectangle
          g.beginFill(backgroundColor, 1);
          g.drawRoundedRect(-cubeSize / 2, -cubeSize / 2, cubeSize, cubeSize, cornerRadius);
          g.endFill();

          // Add hover effect
          if (canOfferDouble) {
            g.lineStyle(0);
            g.beginFill(0xffffff, 0.1);
            g.drawRoundedRect(-cubeSize / 2, -cubeSize / 2, cubeSize, cubeSize, cornerRadius);
            g.endFill();
          }
        }}
      />

      {/* Cube value text */}
      <Text text={cubeValue.toString()} style={textStyle} anchor={0.5} x={0} y={0} />
    </Container>
  );
}
